import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

const navLinks = [
  { to: '/', label: 'Home', end: true },
  { to: '/about', label: 'About' },
  { to: '/whyus', label: 'Why Us' },
  { to: '/services', label: 'Services' },
  { to: '/contact', label: 'Contact' },
  { to: '/associate-request', label: 'Become Associate' }
];
const authLinks = [
  { to: '/register', label: 'Register' },
  { to: '/login', label: 'Login' }
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile nav on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className="main-website-navbar"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1000,
        background: scrolled
          ? '#fff'
          : 'rgba(0,0,0,0.18)',
        boxShadow: scrolled ? '0 2px 12px rgba(0,0,0,0.07)' : 'none',
        backdropFilter: 'blur(2px)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        transition: 'background 0.25s, box-shadow 0.25s'
      }}
    >
      <nav className="container-fluid d-flex align-items-center justify-content-between py-2">
        <Link to="/" className="logo d-flex align-items-center" style={{ textDecoration: 'none' }}>
          <img 
            src="/assets/img/cv-connect_logo.png" 
            alt="CV-Connect Logo" 
            style={{
              height: 'clamp(28px, 4vw, 40px)',
              width: 'clamp(28px, 4vw, 40px)',
              marginRight: 'clamp(6px, 1.5vw, 12px)',
              borderRadius: '50%'
            }}
          />
          <h1
            className="sitename"
            style={{
              color: scrolled ? '#fd680e' : '#fff',
              fontWeight: 600,
              fontSize: 'clamp(20px, 4vw, 32px)',
              margin: 0,
              letterSpacing: 1,
              transition: 'color 0.25s'
            }}
          >
            CV‑Connect
          </h1>
        </Link>
        {/* Desktop Nav */}
        <ul className="d-none d-md-flex align-items-center mb-0" style={{ listStyle: 'none', gap: 12, fontSize: 17 }}>
          {navLinks.map((link) => (
            <li key={link.to}>
              <NavLink
                to={link.to}
                end={link.end || false}
                className={({ isActive }) =>
                  isActive ? 'nav-link nav-cta active' : 'nav-link nav-cta'
                }
                style={({ isActive }) => ({
                  color: scrolled
                    ? isActive ? '#fd680e' : '#444'
                    : isActive ? '#fd680e' : '#fff',
                  fontWeight: isActive ? 700 : 500,
                  textDecoration: 'none',
                  padding: '8px 16px',
                  borderRadius: 30,
                  background: isActive
                    ? 'rgba(253,104,14,0.10)'
                    : scrolled
                      ? 'rgba(253,104,14,0.06)'
                      : 'rgba(255,255,255,0.08)',
                  border: isActive
                    ? '2px solid #fd680e'
                    : '2px solid transparent',
                  marginRight: 2,
                  marginLeft: 2,
                  whiteSpace: 'nowrap',
                  fontSize: 16,
                  lineHeight: 1.2,
                  transition: 'transform 0.18s, box-shadow 0.18s, color 0.18s, background 0.18s, border 0.18s'
                })}
              >
                {link.label}
              </NavLink>
            </li>
          ))}
          {/* Spacer */}
          <li style={{ width: 12 }}></li>
          {authLinks.map((link) => (
            <li key={link.to}>
              <NavLink
                to={link.to}
                className={({ isActive }) =>
                  isActive ? 'nav-link nav-cta active' : 'nav-link nav-cta'
                }
                style={({ isActive }) => ({
                  color: isActive
                    ? '#fff'
                    : scrolled
                      ? '#fd680e'
                      : '#fd680e',
                  fontWeight: 700,
                  textDecoration: 'none',
                  padding: '8px 20px',
                  borderRadius: 30,
                  background: isActive
                    ? '#fd680e'
                    : 'rgba(253,104,14,0.10)',
                  border: '2px solid #fd680e',
                  marginRight: 2,
                  marginLeft: 2,
                  whiteSpace: 'nowrap',
                  fontSize: 16,
                  lineHeight: 1.2,
                  transition: 'transform 0.18s, box-shadow 0.18s, color 0.18s, background 0.18s, border 0.18s'
                })}
              >
                {link.label}
              </NavLink>
            </li>
          ))}
        </ul>
        {/* Hamburger for mobile */}
        <button
          className="d-md-none"
          style={{
            background: 'none',
            border: 'none',
            color: scrolled ? '#fd680e' : '#fff',
            fontSize: 'clamp(24px, 5vw, 32px)',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle navigation"
        >
          <i className={open ? 'bi bi-x' : 'bi bi-list'}></i>
        </button>
      </nav>
      {/* Mobile Nav */}
      {open && (
        <div className="d-md-none mobile-nav-menu" data-testid="mobile-nav-menu">
          <ul>
            {[...navLinks, ...authLinks].map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  end={link.end || false}
                  className="nav-link nav-cta"
                  onClick={() => setOpen(false)}
                >
                  {link.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Button animation styles */}
      <style>{`
        .nav-cta:hover, .nav-cta:focus {
          transform: scale(1.07);
          box-shadow: 0 4px 24px rgba(253,104,14,0.18);
          z-index: 2;
        }
      `}</style>
    </header>
  );
};

export default Navbar;
